import { useState, useEffect } from 'react';
import { BarChart3, Package, CheckCircle, FileText, MapPin, Activity } from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { rotuloAreaAtivo, rotuloStatusAtivo } from '@/lib/i18n';

interface Estatisticas {
  total: number;
  porStatus: Array<{ status: string; quantidade: number }>;
  porArea: Array<{ area: string; quantidade: number }>;
  porTipo: Array<{ tipo: string; quantidade: number }>;
  comApr: number;
  comAmt: number;
}

/** Quantos itens as listas de Top Áreas / Top Tipos mostram. */
const LIMITE_LISTA = 15;

export function Estatisticas() {
  const { projeto } = useProjeto();
  const { t } = useI18n();
  const [estatisticas, setEstatisticas] = useState<Estatisticas | null>(null);
  const [loading, setLoading] = useState(true);

  const carregarEstatisticas = async () => {
    if (!projeto) return;

    try {
      setLoading(true);
      const response = await apiService.obterEstatisticasAtivos(projeto.id);
      if (response.success && response.data) {
        setEstatisticas(response.data as Estatisticas);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projeto) {
      carregarEstatisticas();
    }
  }, [projeto]);

  const stats = [
    {
      id: 'totalAtivos',
      title: t('estatisticas.kpi.totalAtivos'),
      value: estatisticas?.total.toString() || '0',
      icon: Package,
      color: 'bg-schmersal-blue',
      iconBg: 'bg-schmersal-blue-light',
      iconColor: 'text-white',
      change: null,
    },
    {
      id: 'comApr',
      title: t('estatisticas.kpi.comApr'),
      value: estatisticas?.comApr.toString() || '0',
      icon: CheckCircle,
      color: 'bg-green-600',
      iconBg: 'bg-green-500',
      iconColor: 'text-white',
      change: estatisticas ? `${((estatisticas.comApr / estatisticas.total) * 100).toFixed(1)}%` : null,
    },
    {
      id: 'comAmt',
      title: t('estatisticas.kpi.comAmt'),
      value: estatisticas?.comAmt.toString() || '0',
      icon: FileText,
      color: 'bg-schmersal-blue-dark',
      iconBg: 'bg-schmersal-blue',
      iconColor: 'text-white',
      change: estatisticas ? `${((estatisticas.comAmt / estatisticas.total) * 100).toFixed(1)}%` : null,
    },
    {
      id: 'areasDiferentes',
      title: t('estatisticas.kpi.areasDiferentes'),
      value: estatisticas?.porArea.length.toString() || '0',
      icon: MapPin,
      color: 'bg-schmersal-gray-dark',
      iconBg: 'bg-schmersal-gray-medium',
      iconColor: 'text-white',
      change: null,
    },
  ];

  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('tramitado') || statusLower.includes('concluído')) {
      return 'bg-green-600 text-white border-green-700';
    }
    if (statusLower.includes('levantar') || statusLower.includes('pendente')) {
      return 'bg-schmersal-yellow text-schmersal-blue border-schmersal-yellow-dark';
    }
    if (statusLower.includes('atender') || statusLower.includes('revisar')) {
      return 'bg-schmersal-blue text-white border-schmersal-blue-dark';
    }
    return 'bg-schmersal-gray-medium text-white border-schmersal-gray-dark';
  };

  const getMaxQuantidade = (items: Array<{ quantidade: number }>) => {
    if (!items || items.length === 0) return 1;
    return Math.max(...items.map(item => item.quantidade));
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-schmersal-blue"></div>
          <p className="mt-4 text-sm text-schmersal-gray-medium font-medium">{t('estatisticas.estado.carregando')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-schmersal-blue dark:bg-schmersal-blue rounded-lg shadow-sm p-6 text-white border border-schmersal-blue-light dark:border-schmersal-blue">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{t('estatisticas.cabecalho.titulo')}</h1>
            <p className="text-schmersal-gray-light dark:text-schmersal-gray-light">
              {t('estatisticas.cabecalho.subtitulo')}
            </p>
          </div>
          <div className="hidden md:block">
            <BarChart3 className="h-16 w-16 text-white opacity-80" />
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.id}
              className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-200 overflow-hidden border border-rg-line dark:border-schmersal-gray-dark"
            >
              <div className={`${stat.color} p-4`}>
                <div className="flex items-center justify-between">
                  <div className={`${stat.iconBg} p-3 rounded-lg`}>
                    <Icon className={`h-6 w-6 ${stat.iconColor}`} />
                  </div>
                  {stat.change && (
                    <div className="text-white text-sm font-semibold">
                      {stat.change}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm font-semibold text-schmersal-gray-dark dark:text-white mb-1">{stat.title}</p>
                <p className="text-3xl font-bold text-schmersal-blue dark:text-white">{stat.value}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Estatísticas por Status */}
      {estatisticas && estatisticas.porStatus.length > 0 && (
        <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm overflow-hidden border border-rg-line dark:border-schmersal-gray-dark">
          <div className="bg-schmersal-blue px-6 py-4 border-b-2 border-schmersal-blue-light">
            <div className="flex items-center">
              <div className="bg-schmersal-blue-light p-2 rounded-lg mr-3">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">{t('estatisticas.porStatus.titulo')}</h3>
                <p className="text-sm text-schmersal-gray-light">{t('estatisticas.porStatus.subtitulo')}</p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {estatisticas.porStatus.map((item) => {
                const maxQuantidade = getMaxQuantidade(estatisticas.porStatus);
                const porcentagem = (item.quantidade / maxQuantidade) * 100;
                const statusLower = item.status.toLowerCase();
                let barColor = 'bg-schmersal-gray-medium';
                if (statusLower.includes('tramitado') || statusLower.includes('concluído')) {
                  barColor = 'bg-green-600';
                } else if (statusLower.includes('levantar') || statusLower.includes('pendente')) {
                  barColor = 'bg-schmersal-yellow';
                } else if (statusLower.includes('atender') || statusLower.includes('revisar')) {
                  barColor = 'bg-schmersal-blue';
                }
                return (
                  <div key={item.status} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold border-2 ${getStatusColor(item.status)}`}>
                          {rotuloStatusAtivo(t, item.status)}
                        </span>
                        <span className="text-sm font-bold text-schmersal-gray-dark dark:text-white">
                          {item.quantidade}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light">
                        {estatisticas.total > 0 ? `${((item.quantidade / estatisticas.total) * 100).toFixed(1)}%` : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark rounded-full h-3 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                        style={{ width: `${porcentagem}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Estatísticas por Área e Tipo - Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Estatísticas por Área */}
        {estatisticas && estatisticas.porArea.length > 0 && (
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm overflow-hidden border border-rg-line dark:border-schmersal-gray-dark">
            <div className="bg-schmersal-gray-dark dark:bg-schmersal-gray-dark px-6 py-4 border-b-2 border-schmersal-gray-medium dark:border-schmersal-gray-dark">
              <div className="flex items-center">
                <div className="bg-schmersal-gray-medium dark:bg-schmersal-blue p-2 rounded-lg mr-3">
                  <MapPin className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('estatisticas.porArea.titulo')}</h3>
                  <p className="text-sm text-schmersal-gray-light">{t('estatisticas.porArea.subtitulo')}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {estatisticas.porArea.slice(0, LIMITE_LISTA).map((item, index) => {
                  const maxQuantidade = getMaxQuantidade(estatisticas.porArea.slice(0, LIMITE_LISTA));
                  const porcentagem = (item.quantidade / maxQuantidade) * 100;
                  return (
                    <div key={item.area} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <span className="text-sm font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light w-6">#{index + 1}</span>
                          <span className="text-sm font-semibold text-schmersal-gray-dark dark:text-white truncate">{rotuloAreaAtivo(t, item.area)}</span>
                        </div>
                        <span className="text-sm font-bold text-schmersal-blue dark:text-white ml-2">{item.quantidade}</span>
                      </div>
                      <div className="w-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-schmersal-gray-dark dark:bg-schmersal-blue transition-all duration-500"
                          style={{ width: `${porcentagem}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {estatisticas.porArea.length > LIMITE_LISTA && (
                <div className="mt-4 pt-4 border-t-2 border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                  <span className="text-sm font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {t('estatisticas.porArea.mostrando', { exibidos: LIMITE_LISTA, total: estatisticas.porArea.length })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Estatísticas por Tipo */}
        {estatisticas && estatisticas.porTipo.length > 0 && (
          <div className="bg-white dark:bg-schmersal-gray-darker rounded-lg shadow-sm overflow-hidden border border-rg-line dark:border-schmersal-gray-dark">
            <div className="bg-schmersal-blue-dark dark:bg-schmersal-blue px-6 py-4 border-b-2 border-schmersal-blue">
              <div className="flex items-center">
                <div className="bg-schmersal-blue p-2 rounded-lg mr-3">
                  <Package className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{t('estatisticas.porTipo.titulo')}</h3>
                  <p className="text-sm text-schmersal-gray-light">{t('estatisticas.porTipo.subtitulo')}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                {estatisticas.porTipo.slice(0, LIMITE_LISTA).map((item, index) => {
                  const maxQuantidade = getMaxQuantidade(estatisticas.porTipo.slice(0, LIMITE_LISTA));
                  const porcentagem = (item.quantidade / maxQuantidade) * 100;
                  return (
                    <div key={item.tipo} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          <span className="text-sm font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light w-6">#{index + 1}</span>
                          <span className="text-sm font-semibold text-schmersal-gray-dark dark:text-white truncate">{item.tipo}</span>
                        </div>
                        <span className="text-sm font-bold text-schmersal-blue dark:text-white ml-2">{item.quantidade}</span>
                      </div>
                      <div className="w-full bg-schmersal-gray-light dark:bg-schmersal-gray-dark rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-schmersal-blue transition-all duration-500"
                          style={{ width: `${porcentagem}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {estatisticas.porTipo.length > LIMITE_LISTA && (
                <div className="mt-4 pt-4 border-t-2 border-schmersal-gray-light dark:border-schmersal-gray-dark text-center">
                  <span className="text-sm font-semibold text-schmersal-gray-medium dark:text-schmersal-gray-light">
                    {t('estatisticas.porTipo.mostrando', { exibidos: LIMITE_LISTA, total: estatisticas.porTipo.length })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
